import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Compass, MagnifyingGlassPlus, MagnifyingGlassMinus, DownloadSimple } from "@phosphor-icons/react";

// "The Big Picture" — full-screen, zoomable view of the value-proposition infographic.
// Works two ways: pass a `trigger` (uncontrolled) OR drive `open`/`onOpenChange` (controlled from a menu).
export default function BigPictureModal({ trigger, open, onOpenChange }) {
  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const [zoom, setZoom] = useState(false);
  const isOpen = controlled ? open : internalOpen;
  const setOpen = (v) => {
    (controlled ? onOpenChange : setInternalOpen)(v);
    if (!v) setZoom(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className="max-w-[96vw] w-[96vw] max-h-[95vh] p-0 gap-0 overflow-hidden bg-[#FDFBF7] border-[#E5E0D6]"
        data-testid="big-picture-modal"
      >
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-[#E5E0D6] pr-14">
          <div className="flex items-center gap-2.5 min-w-0">
            <Compass size={22} weight="fill" className="text-[#B47A3C] shrink-0" />
            <div className="min-w-0">
              <DialogTitle className="font-serif-display text-xl sm:text-2xl leading-none">The Big Picture</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-[#737373] mt-1 truncate">
                Vintage Vantage — the mission at 30,000 feet
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setZoom((z) => !z)}
              data-testid="big-picture-zoom-toggle"
              className="inline-flex items-center gap-1.5 text-sm font-medium border border-[#D8D3C8] bg-white px-3 py-1.5 rounded-md hover:bg-[#F5F3ED] transition-colors"
            >
              {zoom ? <MagnifyingGlassMinus size={16} /> : <MagnifyingGlassPlus size={16} />}
              <span className="hidden sm:inline">{zoom ? "Fit to screen" : "Zoom in"}</span>
            </button>
            <a
              href="/big_picture.png"
              download="vintage-vantage-the-big-picture.png"
              data-testid="big-picture-download"
              className="inline-flex items-center gap-1.5 text-sm font-medium border border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] px-3 py-1.5 rounded-md hover:bg-black transition-colors"
            >
              <DownloadSimple size={16} />
              <span className="hidden sm:inline">Download</span>
            </a>
          </div>
        </div>
        <div className="overflow-auto bg-[#F5F3ED]" style={{ maxHeight: "calc(95vh - 66px)" }}>
          <div className={zoom ? "" : "flex items-start justify-center p-3"}>
            <img
              src="/big_picture.png"
              alt="Vintage Vantage — The Big Picture: one strategy, many paths, exponentially more outcomes"
              data-testid="big-picture-image"
              onClick={() => setZoom((z) => !z)}
              className={zoom ? "max-w-none w-[2200px] cursor-zoom-out" : "w-full h-auto max-w-[1536px] cursor-zoom-in"}
            />
          </div>
          <div className="border-t border-[#E5E0D6] bg-[#FDFBF7] px-6 py-9 sm:py-12 text-center" data-testid="big-picture-quote">
            <Compass size={20} weight="fill" className="text-[#B47A3C] mx-auto mb-4 opacity-70" />
            <p className="font-serif-display text-xl sm:text-2xl lg:text-[28px] leading-snug text-[#0D1B2A] max-w-3xl mx-auto">
              When seller knowledge becomes <span className="text-[#B47A3C]">portable</span>, valuable effort becomes a <span className="text-[#B47A3C]">force multiplier</span> of time, expertise, reach, and profitability.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
